// In-memory CRUD store for demo mode.
// Mimics the shape of supabaseData / API responses so components can't tell the difference.
// Platform-agnostic: uses Math.random() instead of crypto.randomUUID() for Hermes compat.

import { getDemoData, DEMO_COMPANY_ID } from './demoData'

/** Generate a simple UUID-like string without crypto.randomUUID() (not available in Hermes) */
const generateId = () => {
  const hex = () => Math.floor(Math.random() * 0x10000).toString(16).padStart(4, '0')
  return `${hex()}${hex()}-${hex()}-${hex()}-${hex()}-${hex()}${hex()}${hex()}`
}

export class DemoStore {
  constructor() {
    this.data = getDemoData()
    this.listeners = new Set()

    // ---- Analytics tracking ----
    this.analytics = {
      started_at: new Date().toISOString(),
      interactions: {},   // e.g. { clients_created: 2, estimates_sent: 1 }
      pages_visited: [],  // unique page paths
      features_used: []   // unique feature labels
    }
  }

  // ---- analytics helpers ----

  /** Increment a named interaction counter (e.g. "clients_created") */
  trackInteraction(action) {
    this.analytics.interactions[action] = (this.analytics.interactions[action] || 0) + 1
  }

  /** Record that a page was visited (deduped) */
  trackPageVisit(path) {
    if (!this.analytics.pages_visited.includes(path)) {
      this.analytics.pages_visited.push(path)
    }
  }

  /** Record that a feature was exercised (deduped) */
  trackFeature(feature) {
    if (!this.analytics.features_used.includes(feature)) {
      this.analytics.features_used.push(feature)
    }
  }

  /** Get a snapshot of all analytics data */
  getAnalytics() {
    const now = new Date()
    const startedAt = new Date(this.analytics.started_at)
    return {
      ...this.analytics,
      exited_at: now.toISOString(),
      duration_seconds: Math.round((now - startedAt) / 1000)
    }
  }

  // ---- reads ----

  getAll(entity) {
    return this.data[entity] || []
  }

  getById(entity, id) {
    return (this.data[entity] || []).find(item => item.id === id) || null
  }

  getByField(entity, field, value) {
    return (this.data[entity] || []).filter(item => item[field] === value)
  }

  // ---- writes ----

  create(entity, record) {
    const now = new Date().toISOString()
    const newRecord = {
      ...record,
      id: record.id || `demo-${entity}-${generateId()}`,
      company_id: record.company_id || DEMO_COMPANY_ID,
      created_at: now,
      updated_at: now
    }

    if (!this.data[entity]) {
      this.data[entity] = []
    }
    this.data[entity] = [...this.data[entity], newRecord]
    this.trackInteraction(`${entity}_created`)
    this.trackFeature(`create_${entity.replace(/s$/, '')}`)
    this._notify()
    return newRecord
  }

  update(entity, id, updates) {
    const now = new Date().toISOString()
    let updated = null

    this.data[entity] = (this.data[entity] || []).map(item => {
      if (item.id === id) {
        updated = { ...item, ...updates, updated_at: now }
        return updated
      }
      return item
    })

    this.trackInteraction(`${entity}_updated`)
    // Track specific status transitions as features
    if (updates.status) {
      this.trackFeature(`${entity.replace(/s$/, '')}_${updates.status}`)
    }
    this._notify()
    return updated
  }

  upsert(entity, record) {
    const existing = this.getById(entity, record.id)
    if (existing) {
      return this.update(entity, record.id, record)
    }
    return this.create(entity, record)
  }

  delete(entity, id) {
    this.data[entity] = (this.data[entity] || []).filter(item => item.id !== id)
    this.trackInteraction(`${entity}_deleted`)
    this._notify()
    return { success: true }
  }

  // ---- estimate-specific helpers ----

  generateEstimateNumber() {
    const estimates = this.getAll('estimates')
    const year = new Date().getFullYear()
    const maxNum = estimates.reduce((max, est) => {
      if (!est.estimate_number) return max
      const parts = est.estimate_number.split('-')
      if (parts.length >= 3) {
        const num = parseInt(parts[2]) || 0
        return Math.max(max, num)
      }
      return max
    }, 0)
    return `EST-${year}-${String(maxNum + 1).padStart(3, '0')}`
  }

  generateInvoiceNumber() {
    const invoices = this.getAll('invoices')
    const year = new Date().getFullYear()
    const maxNum = invoices.reduce((max, inv) => {
      if (!inv.invoice_number) return max
      const parts = inv.invoice_number.split('-')
      if (parts.length >= 3) {
        const num = parseInt(parts[2]) || 0
        return Math.max(max, num)
      }
      return max
    }, 0)
    return `INV-${year}-${String(maxNum + 1).padStart(3, '0')}`
  }

  // ---- subscription for React re-renders ----

  subscribe(listener) {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  _notify() {
    this.listeners.forEach(fn => fn())
  }

  // ---- reset to initial state ----

  reset() {
    this.data = getDemoData()
    this._notify()
  }
}
